// shell/components/entity-fields/form-renderers/duration-input.ts

import { createElement } from '../../../utils/dom';
import type { DurationFormField, DurationUnit, DurationValue } from '../../entity-view/types';
import type { FormRendererResult } from './types';

const DEFAULT_UNITS: DurationUnit[] = ['days', 'months', 'years'];
const NEVER_KEY = '__never__';

function parseValue(v: unknown): DurationValue | null {
    if (v == null) return null;
    if (typeof v === 'object' && 'unit' in v && 'amount' in v) {
        const u = (v as { unit: unknown }).unit;
        const a = Number((v as { amount: unknown }).amount);
        if ((u === 'days' || u === 'months' || u === 'years') && Number.isFinite(a)) {
            return { unit: u, amount: a };
        }
    }
    return null;
}

/**
 * Renders a compound "expires in X {unit|never}" control: a unit
 * selector tightly paired with a narrow numeric amount. Selecting
 * the optional "never" entry hides the amount and yields a `null`
 * value to signal "no expiration".
 */
export function renderDurationInput<T>(
    field: DurationFormField<T>,
    initialValue: unknown,
    onChange: (value: DurationValue | null) => void,
): FormRendererResult {
    const units = field.units ?? DEFAULT_UNITS;
    const allowNever = field.allowNever === true;
    const neverLabel = field.neverLabel ?? 'never';
    const min = field.min ?? 1;
    const max = field.max ?? 999;

    const container = createElement('div', {
        className: 'ev-field-input ev-field-input--duration',
    });

    let current: DurationValue | null = parseValue(initialValue);
    if (current === null && !allowNever) {
        current = { unit: units[0], amount: min };
    }

    const select = createElement('select', {
        className: 'ev-select ev-duration-unit',
        attributes: {
            ...(field.disabled ? { disabled: '' } : {}),
            ...(field.required ? { 'aria-required': 'true' } : {}),
        },
    }) as HTMLSelectElement;
    for (const u of units) {
        const opt = document.createElement('option');
        opt.value = u;
        opt.textContent = u;
        select.appendChild(opt);
    }
    if (allowNever) {
        const opt = document.createElement('option');
        opt.value = NEVER_KEY;
        opt.textContent = neverLabel;
        select.appendChild(opt);
    }

    const numInput = createElement('input', {
        className: 'ev-input ev-input--number ev-duration-amount',
        attributes: {
            type: 'number',
            min: String(min),
            max: String(max),
            step: '1',
            'aria-label': 'amount',
            ...(field.disabled ? { disabled: '' } : {}),
        },
    }) as HTMLInputElement;

    function syncUI(): void {
        if (current === null) {
            select.value = NEVER_KEY;
            numInput.style.display = 'none';
        } else {
            select.value = current.unit;
            numInput.style.display = '';
            // Avoid clobbering the cursor while the user is typing.
            if (numInput.value !== String(current.amount)) {
                numInput.value = String(current.amount);
            }
        }
    }

    function clamp(n: number): number {
        return Math.max(min, Math.min(max, Math.floor(n)));
    }

    select.addEventListener('change', () => {
        if (select.value === NEVER_KEY) {
            current = null;
        } else {
            const amt = current?.amount ?? min;
            current = { unit: select.value as DurationUnit, amount: clamp(amt) };
        }
        syncUI();
        onChange(current);
    });

    numInput.addEventListener('input', () => {
        if (numInput.value === '' || current === null) return;
        const v = parseInt(numInput.value, 10);
        if (!Number.isFinite(v)) return;
        current = { unit: current.unit, amount: clamp(v) };
        // Don't rewrite the input value here — that would move the
        // caret while the user is typing.
        onChange(current);
    });

    container.appendChild(select);
    container.appendChild(numInput);
    syncUI();

    return {
        element: container,
        getValue: () => current,
        setValue: (value: unknown) => {
            current = parseValue(value);
            syncUI();
        },
        validate: async () => true,
        focus: () => select.focus(),
        setDisabled: (disabled: boolean) => {
            select.disabled = disabled;
            numInput.disabled = disabled;
        },
        dispose: () => {
            // Listeners are on elements that get removed with the container.
        },
    };
}
